import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Pagination } from '@/Models/Page';
import {
    convertSearchFiltersToIdentifiedFilters,
    DefaultRefineSearchFilters,
    RefineSearchFilters,
    useSearchFilters,
} from '../../../api/useSearchFilters';
import {
    convertSearchCriteriaResponseToSearchCriteriaFilters,
    useSearchCriteria,
} from '../../../api/useSearchCriteria';
import { IdentifiedProfileOptions, useIdentifiedProfiles } from '../../../api/useIdentifiedProfiles';
import { usePagination, useRecruitmentId } from '@/Hooks';

export const minLocationRadius: number = 1;
export const minYearsOfExperience: number = 0;
export const maxYearsOfExperience: number = 30;
export const identifiedListPaginationSizeOptions = [20, 50, 75, 100, 150];
const identifiedListDefaultPageSize = identifiedListPaginationSizeOptions[2]; // 75 is default for identified list.

export interface IdentifiedListContextType extends ReturnType<typeof useIdentifiedProfiles> {
    pagination: Pagination;
    setPagination: React.Dispatch<React.SetStateAction<Pagination>>;
    refineSearchFilters: RefineSearchFilters;
    setRefineSearchFilters: React.Dispatch<React.SetStateAction<RefineSearchFilters>>;
    options: IdentifiedProfileOptions;
    setOptions: React.Dispatch<React.SetStateAction<IdentifiedProfileOptions>>;
}

export const IdentifiedListContext = createContext<IdentifiedListContextType>(undefined!);

export const useIdentifiedListContextProvider = (): IdentifiedListContextType => {
    const recruitmentId = useRecruitmentId();
    const [options, setOptionsState] = useState<IdentifiedProfileOptions>({});
    const [refineSearchFilters, setRefineSearchFilters] = useState<RefineSearchFilters>(DefaultRefineSearchFilters);
    const [pagination, setPagination] = usePagination(identifiedListDefaultPageSize);

    const identifiedProfiles = useIdentifiedProfiles(pagination.limit, pagination.offset, options);
    const { data: searchFiltersData, isLoading: isSearchFiltersLoading } = useSearchFilters(recruitmentId!);
    const { data: searchCriteriaData, isLoading: isSearchCriteriaLoading } = useSearchCriteria(recruitmentId!);

    useEffect(() => {
        if (!isSearchFiltersLoading && searchFiltersData) {
            const convertedFilters = convertSearchFiltersToIdentifiedFilters(searchFiltersData);
            setRefineSearchFilters((state) => ({ ...convertedFilters, rankingCriteria: state.rankingCriteria }));
        }
    }, [searchFiltersData, isSearchFiltersLoading]);

    useEffect(() => {
        if (!isSearchCriteriaLoading && searchCriteriaData && searchCriteriaData.criteria.length > 0) {
            const convertedSearchCriteria = convertSearchCriteriaResponseToSearchCriteriaFilters(searchCriteriaData);
            setRefineSearchFilters((state) => ({ ...state, rankingCriteria: convertedSearchCriteria.rankingCriteria }));
        }
    }, [searchCriteriaData, isSearchCriteriaLoading]);

    const setOptions: IdentifiedListContextType['setOptions'] = useCallback(
        (dispatch) => {
            setPagination((p) => ({ ...p, offset: 0 }));
            return setOptionsState(dispatch);
        },
        [setOptionsState, setPagination],
    );

    return {
        pagination,
        setPagination,
        refineSearchFilters,
        setRefineSearchFilters,
        options,
        setOptions,
        ...identifiedProfiles,
    };
};

export const useIdentifiedListContext = () => {
    const context = useContext(IdentifiedListContext);

    if (!context) {
        throw new Error('useIdentifiedListContext must be used inside IdentifiedListContextProvider');
    }

    return context;
};
